import { useEffect, useRef } from "react";
import {
  collection,
  doc,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  where
} from "firebase/firestore";
import { db } from "../firebase";
import { getTaskDueDate } from "../utils/time";
import { createActionId } from "../utils/actions";

export const useAutoFailTasks = ({ familyId, familySettings, canManage = false }) => {
  const inFlight = useRef(new Set());
  const openTasksRef = useRef([]);
  const autoFailEnabled = familySettings?.autoFailEnabled !== false;
  const isProFamily = familySettings?.isPro !== false;
  const familyGraceMins = Number(familySettings?.autoFailGraceMins || 0);
  const clarificationsEnabled = familySettings?.clarificationsEnabled !== false;

  useEffect(() => {
    if (!familyId || !familySettings) return undefined;
    if (!canManage) return undefined;
    if (!autoFailEnabled) return undefined;
    if (!isProFamily) return undefined;

    const familyGraceMs = familyGraceMins * 60 * 1000;
    const inFlightSet = inFlight.current;

    const tasksRef = collection(db, "families", familyId, "tasks");

    const evaluateTasks = () => {
      const now = new Date();
      openTasksRef.current.forEach((task) => {
        const taskId = task.id;
        if (!taskId || inFlightSet.has(taskId)) return;

        const due = getTaskDueDate(task, now);
        if (!due) return;

        if (now.getTime() < due.getTime() + familyGraceMs) return;

        inFlightSet.add(taskId);
        const actionId = createActionId();
        const taskRef = doc(db, "families", familyId, "tasks", taskId);
        const historyRef = doc(db, "families", familyId, "history", actionId);

        runTransaction(db, async (tx) => {
          const latest = await tx.get(taskRef);
          if (!latest.exists()) return;
          const latestData = latest.data() || {};
          if (latestData.status !== "open") return;

          tx.update(taskRef, {
            status: "failed",
            needsClarification: clarificationsEnabled,
            autoFailedAt: serverTimestamp()
          });
          tx.set(historyRef, {
            actionId,
            type: "fail",
            autoFail: true,
            title: latestData.title,
            kidName: latestData.assignedToName || latestData.kidName || "Unknown",
            createdAt: serverTimestamp()
          });
        })
          .catch((error) => {
            console.warn("Auto-fail client sweep error:", error);
          })
          .finally(() => {
            inFlightSet.delete(taskId);
          });
      });
    };

    const unsub = onSnapshot(
      query(tasksRef, where("status", "==", "open")),
      (snap) => {
        openTasksRef.current = snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }));
        evaluateTasks();
      }
    );

    const interval = setInterval(evaluateTasks, 60 * 1000);

    return () => {
      inFlightSet.clear();
      openTasksRef.current = [];
      clearInterval(interval);
      unsub();
    };
  }, [
    canManage,
    familyId,
    familySettings,
    autoFailEnabled,
    familyGraceMins,
    clarificationsEnabled,
    isProFamily
  ]);
};
